import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react"

import * as Sentry from "@sentry/react"
import { addDays, endOfWeek, format, isToday, startOfWeek } from "date-fns"
import { ChevronDown, ChevronRight } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { ukDateOf } from "@/constants/sheriffOptions"
import { useCarParks } from "@/hooks/useCarParks"
import { useDepots } from "@/hooks/useDepots"
import { type ShiftWithDisplay, useShiftDialog } from "@/hooks/useShiftDialog"
import { useShifts } from "@/hooks/useShifts"
import { ukDayEndOf, ukDayStartOf } from "@/lib/ukCalendarDate"
import type { Shift } from "@/services/shiftsService"
import { Person } from "@/types/person"
import { exportShiftsToCSV } from "@/utils/csvExportUtils"
import { getShiftDisplayStatus } from "@/utils/shiftDisplayStatus"

import ShiftDetailsDialog from "../shift/ShiftDetailsDialog"
import ShiftBlock from "../ui/ShiftBlock"
import { getShiftTypeIcon } from "../ui/shiftTypeIcons"
import { WeeklyScheduleSkeleton } from "./SkeletonLoaders"
import { UNKNOWN_DEPOT, formatClockInLocation } from "./clockInLocation"

const { logger } = Sentry

interface SheriffKanbanBoardProps {
  date: Date
  scheduleType: "delivery" | "collection" | "sortation" | "internal"
  filters: {
    location: string
    employer: string
    type: string
    areas: string[]
  }
  searchTerm: string
  onCountChange?: (count: number) => void
  people: Person[]
  peopleLoading: boolean
}

export interface SheriffKanbanBoardRef {
  exportToCSV: () => void
}

const SheriffKanbanBoard = forwardRef<SheriffKanbanBoardRef, SheriffKanbanBoardProps>(
  (
    {
      date,
      scheduleType: _scheduleType,
      filters,
      searchTerm,
      onCountChange,
      people,
      peopleLoading,
    },
    ref,
  ) => {
    // Use shared shift dialog hook
    const {
      shiftDetailsOpen,
      setShiftDetailsOpen,
      selectedShift,
      detailsMode,
      isSaving,
      handleShiftClick,
      handleShiftSave,
      handleForceClockIn,
      handleForceClockOut,
    } = useShiftDialog()
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

    // Local toggles for visibility controls
    const [hideEmpty, setHideEmpty] = useState(false)
    const [hideCancelled, setHideCancelled] = useState(false)
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

    // Use date prop directly instead of local state
    const weekStart = startOfWeek(date, { weekStartsOn: 1 }) // Monday
    const weekEnd = endOfWeek(date, { weekStartsOn: 1 })
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

    // Fetch shifts for the entire week with Sentry tracking
    const {
      data: shifts = [],
      isLoading,
      isFetching,
      error: shiftsError,
    } = useShifts({
      startTime: ukDayStartOf(weekStart).toISOString(),
      endTime: ukDayEndOf(weekEnd).toISOString(),
      location: filters.location || undefined,
      employer: filters.employer !== "all" ? filters.employer : undefined,
      type: filters.type !== "all" ? filters.type : undefined,
      areas: filters.areas && filters.areas.length ? filters.areas : undefined,
      page: 1,
      pageSize: 10000,
    })
    const { data: depots = [], isLoading: depotsLoading } = useDepots()
    const { data: carParks = [], isLoading: carParksLoading } = useCarParks()
    // Until both lists arrive, a lookup miss would read as "Unknown depot"
    const clockLocationsReady = !depotsLoading && !carParksLoading
    const depotNameById = useMemo(() => {
      const map = new Map<string, string>()
      depots.forEach((depot) => {
        map.set(depot.id, depot.name)
      })
      return map
    }, [depots])
    const carParkById = useMemo(
      () => new Map(carParks.map((carPark) => [carPark.id, carPark])),
      [carParks],
    )
    const personNameById = useMemo(() => {
      const map = new Map<string, string>()
      people.forEach((p) => {
        if (p.id && p.person) map.set(p.id, p.person)
      })
      return map
    }, [people])

    // Track API call performance and errors
    useEffect(() => {
      if (!isLoading && !isFetching) {
        Sentry.startSpan(
          {
            op: "http.client",
            name: "Fetch Sheriff Shifts",
          },
          (span) => {
            span.setAttribute("week_start", weekStart.toISOString())
            span.setAttribute("week_end", weekEnd.toISOString())
            span.setAttribute("location", filters.location || "all")
            span.setAttribute("employer", filters.employer || "all")
            span.setAttribute("shifts_count", shifts.length)

            if (shiftsError) {
              logger.error("Failed to fetch shifts", {
                error: shiftsError,
                filters,
                weekStart: weekStart.toISOString(),
              })
              Sentry.captureException(shiftsError)
            } else {
              logger.debug(
                logger.fmt`Fetched ${shifts.length} shifts for week starting ${weekStart.toISOString()}`,
              )
            }
          },
        )
      }
    }, [isLoading, isFetching, shifts.length, shiftsError])

    // Track if we've loaded at least once
    useEffect(() => {
      if (!isLoading && shifts.length >= 0) {
        setHasLoadedOnce(true)
      }
    }, [isLoading, shifts.length])

    // Notify parent about fetched count
    useEffect(() => {
      onCountChange?.(shifts.length)
    }, [shifts.length, onCountChange])

    // Add display status to shifts without mapping
    const shiftsWithDisplay: ShiftWithDisplay[] = useMemo(() => {
      return shifts.map((s) => ({
        ...s,
        displayStatus: getShiftDisplayStatus(s),
      }))
    }, [shifts])

    const isCancelledStatus = (status: ShiftWithDisplay["displayStatus"]) =>
      status === "cancelled" || status === "unnotified-absence"

    const compareShifts = (a: ShiftWithDisplay, b: ShiftWithDisplay) => {
      if (isCancelledStatus(a.displayStatus) === isCancelledStatus(b.displayStatus)) {
        const timeCompare = a.startTime.localeCompare(b.startTime)
        if (timeCompare !== 0) return timeCompare
        const aName = a.people?.person || "Unassigned"
        const bName = b.people?.person || "Unassigned"
        return aName.localeCompare(bName)
      }
      return isCancelledStatus(a.displayStatus) ? 1 : -1
    }

    const dayKeys = useMemo(() => weekDays.map((day) => format(day, "yyyy-MM-dd")), [weekDays])

    // Counts should ignore search but respect hide toggles, matching day view semantics.
    const shiftsForCounts = useMemo(() => {
      return shiftsWithDisplay.filter((shift) => {
        const personName = shift.people?.person || "Unassigned"
        const hideBecauseEmpty =
          hideEmpty && (shift.displayStatus === "empty" || personName === "Unassigned")
        const hideBecauseCancelled = hideCancelled && isCancelledStatus(shift.displayStatus)
        return !hideBecauseEmpty && !hideBecauseCancelled
      })
    }, [shiftsWithDisplay, hideEmpty, hideCancelled])

    const filteredShiftsForWeek = useMemo(() => {
      return shiftsForCounts
        .filter((shift) => {
          const personName = shift.people?.person || "Unassigned"
          return (
            personName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            shift.id.includes(searchTerm)
          )
        })
        .sort(compareShifts)
    }, [shiftsForCounts, searchTerm])

    const groupedShiftsByArea = useMemo(() => {
      type DayBucket = {
        shifts: ShiftWithDisplay[]
        filled: number
        total: number
      }
      type AreaBucket = {
        area: string
        days: Record<string, DayBucket>
      }

      const visibleAreas = new Set(filteredShiftsForWeek.map((s) => s.area || "Unknown"))
      const areaBuckets: Record<string, AreaBucket> = {}

      const ensureArea = (area: string) => {
        if (areaBuckets[area]) return
        const days: Record<string, DayBucket> = {}
        dayKeys.forEach((k) => {
          days[k] = { shifts: [], filled: 0, total: 0 }
        })
        areaBuckets[area] = { area, days }
      }

      visibleAreas.forEach((area) => ensureArea(area))

      shiftsForCounts.forEach((shift) => {
        const area = shift.area || "Unknown"
        if (!visibleAreas.has(area)) return
        const dayKey = ukDateOf(new Date(shift.startTime))
        const day = areaBuckets[area]?.days[dayKey]
        if (!day) return
        day.total += 1
        if (
          shift.displayStatus !== "empty" &&
          shift.displayStatus !== "open" &&
          !isCancelledStatus(shift.displayStatus)
        ) {
          day.filled += 1
        }
      })

      filteredShiftsForWeek.forEach((shift) => {
        const area = shift.area || "Unknown"
        if (!visibleAreas.has(area)) return
        const dayKey = ukDateOf(new Date(shift.startTime))
        const day = areaBuckets[area]?.days[dayKey]
        if (!day) return
        day.shifts.push(shift)
      })

      return Object.values(areaBuckets).sort((a, b) => a.area.localeCompare(b.area))
    }, [dayKeys, filteredShiftsForWeek, shiftsForCounts])

    useEffect(() => {
      setExpandedSections((prev) => {
        const next: Record<string, boolean> = {}
        groupedShiftsByArea.forEach((group) => {
          next[group.area] = prev[group.area] ?? true
        })
        const changed =
          Object.keys(next).length !== Object.keys(prev).length ||
          Object.keys(next).some((k) => next[k] !== prev[k])
        return changed ? next : prev
      })
    }, [groupedShiftsByArea])

    const toggleSection = (area: string) => {
      setExpandedSections((prev) => ({ ...prev, [area]: !prev[area] }))
    }

    // Expose export function to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        exportToCSV: () => {
          // Convert ShiftWithDisplay back to Shift for export
          const shiftsToExport: Shift[] = filteredShiftsForWeek.map((shift) => ({
            id: shift.id,
            peopleId: shift.peopleId,
            startTime: shift.startTime,
            endTime: shift.endTime,
            minutesLate: shift.minutesLate,
            awol: shift.awol,
            status: shift.status,
            area: shift.area,
            type: shift.type,
            employer: shift.employer,
            location: shift.location,
            depotId: shift.depotId,
            vehicleReg: shift.vehicle?.reg ?? null,
            notes: shift.notes,
            preassignedRoute: shift.preassignedRoute,
            cancelledReason: shift.cancelledReason,
            cancelledTime: shift.cancelledTime,
            notifications: shift.notifications,
            lastEditedBy: shift.lastEditedBy,
            people: shift.people,
            timesheet: shift.timesheet,
          }))
          const filename = `shifts_weekly_${format(weekStart, "yyyy-MM-dd")}_to_${format(weekEnd, "yyyy-MM-dd")}_${shiftsToExport.length}.csv`
          exportShiftsToCSV(shiftsToExport, filename)
        },
      }),
      [filteredShiftsForWeek, weekStart, weekEnd],
    )

    return (
      <div className="space-y-4">
        {/* Visibility Controls */}
        <div className="flex items-center justify-between gap-6 bg-palette-jungleGreen000 border border-palette-jungleGreen200 rounded-md px-3 py-2">
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-palette-jungleGreen700">
              <Switch checked={hideEmpty} onCheckedChange={setHideEmpty} />
              Hide Empty
            </label>
            <label className="flex items-center gap-2 text-sm text-palette-jungleGreen700">
              <Switch checked={hideCancelled} onCheckedChange={setHideCancelled} />
              Hide Cancelled
            </label>
          </div>
          {isFetching && hasLoadedOnce && (
            <div className="flex items-center gap-2 text-sm text-palette-jungleGreen600">
              <Spinner size="sm" />
              <span>Updating list...</span>
            </div>
          )}
        </div>

        {/* Week Matrix: days header, then collapsible area groups */}
        <div className="w-full overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
          <div className="space-y-3 relative" style={{ minHeight: "calc(100vh - 250px)" }}>
            <div className="grid grid-cols-7 gap-1.5 sticky top-0 z-20 bg-white/95 backdrop-blur-sm p-1">
              {weekDays.map((day) => {
                const dayKey = format(day, "yyyy-MM-dd")
                const isTodayColumn = isToday(day)
                return (
                  <div
                    key={dayKey}
                    className={`rounded-md border px-3 py-1.5 relative overflow-hidden ${
                      isTodayColumn
                        ? "bg-palette-jungleGreen800 border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.35)]"
                        : "bg-palette-jungleGreen700 border-palette-jungleGreen700"
                    }`}
                  >
                    <div className="flex items-center justify-center min-w-0">
                      <span
                        className={`text-sm font-semibold truncate text-center ${
                          isTodayColumn ? "text-amber-300" : "text-white"
                        }`}
                      >
                        {format(day, "EEEE do")}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>

            {isLoading ? (
              <WeeklyScheduleSkeleton />
            ) : groupedShiftsByArea.length === 0 ? (
              <div className="text-center py-10 text-palette-jungleGreen500 text-sm">No blocks</div>
            ) : (
              <div className="space-y-3">
                {groupedShiftsByArea.map((group) => {
                  const isExpanded = expandedSections[group.area] !== false
                  const groupTotal = dayKeys.reduce(
                    (sum, dayKey) => sum + group.days[dayKey].total,
                    0,
                  )
                  const groupFilled = dayKeys.reduce(
                    (sum, dayKey) => sum + group.days[dayKey].filled,
                    0,
                  )
                  const AreaIcon = getShiftTypeIcon(group.area)
                  return (
                    <div key={group.area} className="border rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleSection(group.area)}
                        className="w-full bg-palette-jungleGreen000 px-4 py-3 border-b cursor-pointer hover:bg-palette-jungleGreen100 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5 text-palette-jungleGreen600" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-palette-jungleGreen600" />
                            )}
                            <h3 className="font-semibold text-palette-jungleGreen900">
                              {group.area} ({groupTotal})
                            </h3>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="secondary" className="text-xs">
                              {groupFilled}/{groupTotal}
                            </Badge>
                            <AreaIcon className="h-4 w-4 text-palette-jungleGreen500" />
                          </div>
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="p-2 space-y-2">
                          <div className="grid grid-cols-7 gap-2">
                            {dayKeys.map((dayKey) => {
                              const dayBucket = group.days[dayKey]
                              return (
                                <div
                                  key={`${group.area}-${dayKey}-count`}
                                  className="rounded-md bg-palette-jungleGreen200 text-center text-sm text-palette-jungleGreen700 py-1"
                                >
                                  {dayBucket.filled}/{dayBucket.total}
                                </div>
                              )
                            })}
                          </div>

                          <div className="grid grid-cols-7 gap-2 items-start">
                            {dayKeys.map((dayKey) => {
                              const dayBucket = group.days[dayKey]
                              return (
                                <div
                                  key={`${group.area}-${dayKey}-cards`}
                                  className="space-y-2 min-h-[24px]"
                                >
                                  {dayBucket.shifts.map((shift) => {
                                    const clockInLocation =
                                      clockLocationsReady && shift.timesheet?.clockinDepotId
                                        ? formatClockInLocation(
                                            depotNameById.get(shift.timesheet.clockinDepotId) ??
                                              null,
                                            carParkById.get(shift.timesheet.clockinCarParkId) ??
                                              null,
                                          )
                                        : null
                                    const clockOutLocation =
                                      clockLocationsReady && shift.timesheet?.clockoutDepotId
                                        ? (depotNameById.get(shift.timesheet.clockoutDepotId) ??
                                          UNKNOWN_DEPOT)
                                        : null
                                    const primaryDriverName = shift.people?.primaryPersonId
                                      ? (personNameById.get(shift.people.primaryPersonId) ?? null)
                                      : null
                                    return (
                                      <ShiftBlock
                                        key={shift.id}
                                        shift={shift}
                                        onClick={() => handleShiftClick(shift)}
                                        clockInLocation={clockInLocation}
                                        clockOutLocation={clockOutLocation}
                                        primaryDriverName={primaryDriverName}
                                      />
                                    )
                                  })}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Shift Details Dialog */}
        <ShiftDetailsDialog
          mode={detailsMode}
          shift={selectedShift}
          open={shiftDetailsOpen}
          onOpenChange={setShiftDetailsOpen}
          onSave={handleShiftSave}
          onForceClockIn={handleForceClockIn}
          onForceClockOut={handleForceClockOut}
          isSaving={isSaving}
          people={people}
          peopleLoading={peopleLoading}
        />
      </div>
    )
  },
)

SheriffKanbanBoard.displayName = "SheriffKanbanBoard"

export default SheriffKanbanBoard
